<template>
  <section class="speakers mobile" :class="{ 'grid-view': showAll }">
    <div class="speakers__content container">
      <h2 class="speakers__title">
        VOICES
        <b>OF THE FUTURE</b>
      </h2>
      <hr class="half-line" />
      <h3 class="speakers__subtitle">
        Our featured speakers
      </h3>
      <p class="speakers__description">
        Learn from industry leaders and innovators shaping the future of investment
      </p>
  
      <div v-if="!showAll" class="speakers__carousel">
        <div 
          class="carousel__container"
          @touchstart="handleTouchStart"
          @touchmove="handleTouchMove"
          @touchend="handleTouchEnd"
          @mousedown="handleMouseDown"
          @mousemove="handleMouseMove"
          @mouseup="handleMouseUp"
          @mouseleave="handleMouseUp"
        >
          <div 
            class="carousel__track" 
            :style="{ transform: transformValue }"
          >
            <div
              class="speakers__cards--single carousel__slide"
              v-for="speaker in speakers"
              :key="speaker.id"
            >
              <button
                v-if="speaker.bio"
                @click="toggleModal(speaker.id)"
                class="speaker-btn"
              >
                <img :src="getImagePath(speaker.image)" :alt="speaker.name" />
                <div class="speaker-container">
                  <h2>{{ speaker.name }}</h2>
                  <h4 v-html="speaker.title"></h4>
                </div>
              </button>

              <div v-else class="speaker-btn">
                <img :src="getImagePath(speaker.image)" :alt="speaker.name" />
                <div class="speaker-container">
                  <h2>{{ speaker.name }}</h2>
                  <h4 v-html="speaker.title"></h4>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Navigation at bottom -->
        <div class="carousel__navigation">
          <button 
            class="carousel__nav --prev" 
            @click="prevSlide"
            :disabled="currentSlide === 0"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M15 18l-6-6 6-6"/>
            </svg>
          </button>

          <button 
            class="carousel__nav --next" 
            @click="nextSlide"
            :disabled="currentSlide >= maxSlide"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M9 18l6-6-6-6"/>
            </svg>
          </button>
        </div>
      </div>

      <div v-else class="speakers__cards">
        <div
          class="speakers__cards--single"
          v-for="speaker in speakers"
          :key="speaker.id"
        >
          <button
            v-if="speaker.bio"
            @click="toggleModal(speaker.id)"
            class="speaker-btn"
          >
            <img :src="getImagePath(speaker.image)" :alt="speaker.name" />
            <div class="speaker-container">
              <h2>{{ speaker.name }}</h2>
              <h4 v-html="speaker.title"></h4>
            </div>
          </button>

          <div v-else class="speaker-btn">
            <img :src="getImagePath(speaker.image)" :alt="speaker.name" />
            <div class="speaker-container">
              <h2>{{ speaker.name }}</h2>
              <h4 v-html="speaker.title"></h4>
            </div>
          </div>
        </div>
      </div>

      <!--<button class="see-more-btn" @click="showAll = !showAll">
        {{ showAll ? 'SEE LESS' : 'SEE ALL SPEAKERS' }}
      </button>-->
    </div>

    <template v-for="speaker in speakers" :key="'modal-' + speaker.id">
      <transition name="fade" appear>
        <div
          class="modal-overlay"
          v-if="modals[speaker.id]"
          @click="toggleModal(speaker.id)"
        ></div>
      </transition>
      <transition name="pop" appear>
        <div class="modal" role="dialog" v-if="modals[speaker.id]">
          <div class="speaker-modal">
            <div class="speaker-thumbnail">
              <img :src="getImagePath(speaker.image)" :alt="speaker.name" />
            </div>
            <div class="speaker-info">
              <h2>{{ speaker.name }}</h2>
              <h3 v-if="speaker.title" v-html="speaker.title"></h3>
              <p class="description" v-html="speaker.bio"></p>
            </div>
          </div>
          <button @click="toggleModal(speaker.id)" class="close">⨉</button>
        </div>
      </transition>
    </template>
  </section>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from "vue";
import speakersData from "./json/speakers.json";

export default {
  name: "Speakers",
  setup() {
    const speakers = ref(speakersData);
    const modals = ref({});
    const showAll = ref(true);
    const currentSlide = ref(0);
    const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1920);
    
    const touchStartX = ref(0);
    const touchEndX = ref(0);
    const isDragging = ref(false);
    const startX = ref(0);
    const hasMoved = ref(false);

    speakers.value.forEach((speaker) => {
      modals.value[speaker.id] = false;
    });

    const toggleModal = (id) => {
      modals.value[id] = !modals.value[id];
    };

    const getImagePath = (imagePath) => {
      return require(`@/assets/speakers/${imagePath}`);
    };

    const slidesPerView = computed(() => {
      if (windowWidth.value <= 768) return 1;
      if (windowWidth.value <= 1024) return 2;
      if (windowWidth.value <= 1280) return 3;
      return 4;
    });

    const maxSlide = computed(() => {
      return Math.max(0, speakers.value.length - slidesPerView.value);
    });

    const transformValue = computed(() => {
      const slideWidthPercent = 100 / slidesPerView.value;
      const translateAmount = currentSlide.value * slideWidthPercent;
      return `translateX(-${translateAmount}%)`;
    });

    const nextSlide = () => {
      if (currentSlide.value < maxSlide.value) {
        currentSlide.value++;
      }
    };

    const prevSlide = () => {
      if (currentSlide.value > 0) {
        currentSlide.value--;
      }
    };

    const handleTouchStart = (e) => {
      touchStartX.value = e.touches[0].clientX;
      hasMoved.value = false;
    };

    const handleTouchMove = (e) => {
      touchEndX.value = e.touches[0].clientX;
      const diff = Math.abs(touchStartX.value - touchEndX.value);
      if (diff > 10) {
        hasMoved.value = true;
      }
    };

    const handleTouchEnd = () => {
      if (!hasMoved.value) {
        touchStartX.value = 0;
        touchEndX.value = 0;
        return;
      }

      const swipeThreshold = 50;
      const diff = touchStartX.value - touchEndX.value;

      if (Math.abs(diff) > swipeThreshold) {
        if (diff > 0) {
          nextSlide();
        } else {
          prevSlide();
        }
      }
      
      touchStartX.value = 0;
      touchEndX.value = 0;
      hasMoved.value = false;
    };

    const handleMouseDown = (e) => {
      isDragging.value = true;
      startX.value = e.clientX;
      hasMoved.value = false;
    };

    const handleMouseMove = (e) => {
      if (!isDragging.value) return;
      
      const diff = Math.abs(startX.value - e.clientX);
      if (diff > 10) {
        hasMoved.value = true;
      }
      
      touchEndX.value = e.clientX;
    };

    const handleMouseUp = () => {
      if (!isDragging.value) return;
      
      // Only navigate if user actually dragged
      if (hasMoved.value) {
        const swipeThreshold = 50;
        const diff = startX.value - touchEndX.value;

        if (Math.abs(diff) > swipeThreshold) {
          if (diff > 0) {
            nextSlide();
          } else {
            prevSlide();
          }
        }
      }

      isDragging.value = false;
      startX.value = 0;
      touchEndX.value = 0;
      hasMoved.value = false;
    };

    const handleResize = () => {
      windowWidth.value = window.innerWidth;
      if (currentSlide.value > maxSlide.value) {
        currentSlide.value = maxSlide.value;
      }
    };

    onMounted(() => {
      window.addEventListener('resize', handleResize);
    });

    onUnmounted(() => {
      window.removeEventListener('resize', handleResize);
    });

    return { 
      speakers, 
      modals, 
      toggleModal, 
      getImagePath,
      showAll,
      currentSlide,
      transformValue,
      maxSlide,
      nextSlide,
      prevSlide,
      handleTouchStart,
      handleTouchMove,
      handleTouchEnd,
      handleMouseDown,
      handleMouseMove,
      handleMouseUp
    };
  },
};
</script>

<style lang="scss" scoped>
@import "@/styles/components/speakers.scss";

:deep(.description > a) {
  color: #fff;
  font-weight: 400;
}
</style>